import {
	clearSensorGroupPairs,
	clearSensorGroups,
	getSensorGroupPairs,
	getSingleSensorGroupById,
	upsertSensorGroupConfig,
} from '@Actions/sensorGroup.action';
import { UpsertSensorGroupForm } from '@Components';
import {
	Button,
	DeleteIcon,
	PageWrapper,
	SplitButton,
	useToggle,
} from '@innovyze/stylovyze';
import { getSelectedSensorIds, getValue } from '@Selectors';
import { useGlobalization } from '@Translations/useGlobalization';
import { MAIN_ROUTES } from '@Types/routes';
import { SensorGroupFormValues } from '@Types/sensorGroup.types';
import { FormikProps } from 'formik';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import useNavigateTo from '../../../hooks/useNavigateTo';
import DeleteSensorGroupModal from './DeleteSensorGroupModal';

export default function UpdateSensorGroup() {
	const { t } = useGlobalization();
	useNavigateTo();
	const history = useHistory();
	const dispatch = useDispatch();

	// create states
	const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);
	const [isValid, setIsValid] = React.useState(false);
	const formRef = useRef<FormikProps<SensorGroupFormValues>>();
	const [sensorGroupName, setSensorGroupName] = React.useState('');
	const { sensorGroupId } = useParams<{ sensorGroupId: string }>();
	const sensorsInGroup = useSelector(getSelectedSensorIds);

	// Redux states
	const {
		upsertSensorGroup: { status },
	} = useSelector(getValue).sensorGroup;

	// Get sensor group data
	useEffect(() => {
		dispatch(clearSensorGroups());
		dispatch(clearSensorGroupPairs());
		dispatch(getSingleSensorGroupById(sensorGroupId));
		dispatch(getSensorGroupPairs(sensorGroupId));
	}, []);

	const { configuredSensors } =
		useSelector(getValue).sensorGroup.sensorGroupById;
	const onSubmit = (values: SensorGroupFormValues) => {
		dispatch(
			upsertSensorGroupConfig({
				...values,
				sensors: values.sensors?.map(sensor => {
					const selectedSensor = configuredSensors?.find(
						s => s.sensorId == sensor,
					);
					return {
						sensorId: sensor,
						channelType: selectedSensor?.channelType || '',
						resolutions: selectedSensor?.resolutions?.split(','),
					};
				}),
				originalSensors: sensorsInGroup,
			}),
		);
	};

	return (
		<PageWrapper
			title={sensorGroupName}
			secondary={
				<div style={{ display: 'flex' }}>
					<Button
						dataCy="cancelSensorGroup"
						variant="contained"
						style={{ marginRight: 8 }}
						color="secondary"
						onClick={() => {
							history.push(MAIN_ROUTES.SensorGroups);
						}}>
						{t('Cancel')}
					</Button>
					<SplitButton
						includeSelectedInMenu={false}
						oneClickMenu
						disabled={status === 'pending'}
						options={[
							{
								cy: 'saveSensorGroup',
								onClick: () => formRef?.current?.submitForm(),
								title: t('Save'),
								disabled: !isValid,
							},
							{
								icon: <DeleteIcon cy="deleteGroup" />,
								onClick: () => {
									toggleDeleteModalOpen();
								},
								title: t('Delete Group'),
							},
						]}
						selectedIcon
					/>
				</div>
			}
			applyPadding
			headerUnderline>
			<DeleteSensorGroupModal
				open={isDeleteModalOpen}
				onCancel={() => toggleDeleteModalOpen()}
				formRef={formRef}
			/>
			<UpsertSensorGroupForm
				onSubmit={onSubmit}
				formRef={formRef}
				setIsValid={setIsValid}
				setSensorGroupName={setSensorGroupName}
			/>
		</PageWrapper>
	);
}
